/**
 * hrCycleLauncher.ts
 *
 * Bootstraps Step 1 (EPR submission) of an hr_v1 cycle. Called by the HR
 * Admin UI when launching a new cycle.
 *
 * Responsibilities:
 *   1. For each employee, build the EPRSubmission skeleton:
 *      - Resolve weights (people-manager vs non-manager — §J #3)
 *      - Compose item arrays from caller-provided OKR/KPI + the canonical
 *        Core + Leadership competencies (CLAUDE.md §C.5, §C.6)
 *   2. Idempotent createSubmission for each
 *   3. Issue self & manager magic-link tokens for each
 *   4. Return the artifacts so HR can copy URLs (Phase 1: manual delivery)
 *
 * Does NOT:
 *   - Query OKR Tracker (caller provides okrItems)
 *   - Send emails / WhatsApp (Phase 2)
 *   - Advance ReviewCycle.currentStep (callsite's responsibility; usually
 *     done after the HR confirms the launch was successful)
 */

import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

import type {
  EPRItem, EPRWeights, EPRSubmissionHeader, CompetencyDef,
} from '../types/eprFramework';
import {
  CORE_COMPETENCIES, LEADERSHIP_SKILLS,
  DEFAULT_WEIGHTS_PEOPLE_MANAGER, DEFAULT_WEIGHTS_NON_MANAGER,
} from '../types/eprFramework';
import {
  createSubmission, submissionId,
} from './eprSubmissionService';
import { getEprTemplate, buildTemplateSnapshot } from './eprTemplateService';
import { omitUndefined } from './firestoreUtils';
import {
  issueEPRSelfTokens, issueEPRManagerTokens, issue360ReviewerTokens,
  type BatchIssueResult,
} from './magicLinkService';
import type {
  Review360Relationship, ReviewerVisibility,
} from '../types/eprFramework';
import type { CycleMagicLinkExpiry } from '../types/magicLinkToken';

// ─── Inputs ─────────────────────────────────────────────────────────────────

export type EmployeeLevel = 'general' | 'supervisor' | 'dept_head';

export interface OkrSeed {
  /** Stable id, e.g. KR id from OKR Tracker. Used as itemId. */
  id: string;
  name: string;
  description?: string;
  target?: string;
}

export interface KpiSeed {
  id: string;
  name: string;
  description?: string;
  target?: string;
}

export interface LaunchStep1EmployeeInput {
  employeeId: string;
  employeeEmail: string;
  employeeName: string;
  supervisorId: string;
  supervisorEmail: string;
  supervisorName: string;
  level: EmployeeLevel;
  /**
   * Canonical form header data (EPR Form.docx). Auto-filled from the
   * directory (jobTitle / unitName) when HR picks the employee; editable
   * only in manual-entry mode. Snapshotted onto the submission so the
   * magic-link form renders it without anyone re-typing it.
   */
  position?: string;
  department?: string;
  /**
   * OKR Tracker /units doc id this employee belongs to. Drives the
   * "OKR Dept" filter on the Step 1 sync picker — a KR appears in the
   * sync list if any of its unit fields (unitId, leadDivisionId,
   * unitIds[]) matches this id. Set per-employee since one Step 1
   * launch can span multiple departments.
   */
  okrTrackerUnitId?: string;
  okrItems: OkrSeed[];
  kpiItems: KpiSeed[];
}

export interface LaunchStep1Input {
  cycleId: string;
  templateId: string;
  employees: LaunchStep1EmployeeInput[];
  /**
   * Review period from the cycle (label + ISO from/to dates). Stamped on
   * every submission header so the form shows "Review Period: From … To …"
   * per the canonical EPR Form.
   */
  reviewPeriod?: {
    label?: string;
    from?: string;
    to?: string;
  };
  cycleExpiryConfig?: CycleMagicLinkExpiry;
  /**
   * When true (default), also update ReviewCycle.currentStep = 1. The caller
   * may pass false to defer the step advance until after the URL distribution
   * step.
   */
  advanceCycleStep?: boolean;
}

export interface LaunchStep1Result {
  cycleId: string;
  submissions: Array<{ employeeId: string; submissionId: string; alreadyExisted: boolean }>;
  selfTokens: BatchIssueResult[];
  managerTokens: BatchIssueResult[];
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function weightsForLevel(level: EmployeeLevel): EPRWeights {
  // Per CLAUDE.md §J #3 / decision answered 2026-05-18:
  // Only 'general' employees skip Leadership and get redistributed weights.
  // Supervisor and dept_head are people-managers and use the canonical
  // 30/40/20/10 split.
  return level === 'general'
    ? DEFAULT_WEIGHTS_NON_MANAGER
    : DEFAULT_WEIGHTS_PEOPLE_MANAGER;
}

// omitUndefined lives in services/firestoreUtils — shared so every
// service path that writes Firestore can strip undefined fields before
// the call (ENGINEERING.md §4).

function buildCompetencyItems(
  defs: CompetencyDef[],
  component: 'core' | 'leadership',
): EPRItem[] {
  return defs.map(d => omitUndefined({
    itemId:      d.key,
    component,
    itemName:    d.name,
    description: d.description,
  }) as EPRItem);
}

function buildOkrItems(seeds: OkrSeed[]): EPRItem[] {
  return seeds.map(s => omitUndefined({
    itemId:      s.id,
    component:   'okr' as const,
    itemName:    s.name,
    description: s.description,
    target:      s.target,
  }) as EPRItem);
}

function buildKpiItems(seeds: KpiSeed[]): EPRItem[] {
  return seeds.map(s => omitUndefined({
    itemId:      s.id,
    component:   'kpi' as const,
    itemName:    s.name,
    description: s.description,
    target:      s.target,
  }) as EPRItem);
}

// ─── Main entry ─────────────────────────────────────────────────────────────

export async function launchStep1(input: LaunchStep1Input): Promise<LaunchStep1Result> {
  const submissions: LaunchStep1Result['submissions'] = [];
  const selfRecipients: Parameters<typeof issueEPRSelfTokens>[0]['recipients'] = [];
  const managerRecipients: Parameters<typeof issueEPRManagerTokens>[0]['recipients'] = [];

  // Freeze the template once per launch — every submission in this batch
  // carries the same snapshot (scale, sources, competencies + rubrics).
  // Null template (legacy templateId not in /eprTemplates) → no snapshot;
  // readers fall back to the §C constants.
  const template = await getEprTemplate(input.templateId);
  const templateSnapshot = template ? buildTemplateSnapshot(template) : undefined;

  for (const emp of input.employees) {
    const weights = weightsForLevel(emp.level);
    const okrItems        = buildOkrItems(emp.okrItems);
    const kpiItems        = buildKpiItems(emp.kpiItems);
    // Competency lists come from the template when it exists (adaptive
    // question lists); the §C constants remain the fallback.
    const coreItems       = buildCompetencyItems(
      template?.coreCompetencies ?? CORE_COMPETENCIES, 'core');
    const leadershipItems = weights.leadership > 0
      ? buildCompetencyItems(template?.leadershipSkills ?? LEADERSHIP_SKILLS, 'leadership')
      : [];

    const id = submissionId(input.cycleId, emp.employeeId);
    const before = await getExistingFlag(id);

    await createSubmission({
      cycleId:        input.cycleId,
      employeeId:     emp.employeeId,
      supervisorId:   emp.supervisorId,
      templateId:     input.templateId,
      weights,
      templateSnapshot,
      okrItems, kpiItems, coreItems, leadershipItems,
      header: omitUndefined({
        employeeName:      emp.employeeName,
        position:          emp.position,
        department:        emp.department,
        supervisorName:    emp.supervisorName,
        reviewPeriodLabel: input.reviewPeriod?.label,
        reviewPeriodFrom:  input.reviewPeriod?.from,
        reviewPeriodTo:    input.reviewPeriod?.to,
      }) as EPRSubmissionHeader,
    });

    submissions.push({ employeeId: emp.employeeId, submissionId: id, alreadyExisted: before });

    selfRecipients.push({
      recipientId:    emp.employeeId,
      recipientEmail: emp.employeeEmail,
      recipientName:  emp.employeeName,
      submissionId:   id,
    });
    managerRecipients.push({
      managerId:     emp.supervisorId,
      managerEmail:  emp.supervisorEmail,
      managerName:   emp.supervisorName,
      submissionId:  id,
    });
  }

  const selfTokens = await issueEPRSelfTokens({
    cycleId:           input.cycleId,
    cycleExpiryConfig: input.cycleExpiryConfig,
    recipients:        selfRecipients,
  });
  const managerTokens = await issueEPRManagerTokens({
    cycleId:           input.cycleId,
    cycleExpiryConfig: input.cycleExpiryConfig,
    recipients:        managerRecipients,
  });

  if (input.advanceCycleStep !== false) {
    await updateDoc(doc(db, 'reviewCycles', input.cycleId), {
      currentStep: 1,
      updatedAt: serverTimestamp(),
    });
  }

  return {
    cycleId: input.cycleId,
    submissions,
    selfTokens,
    managerTokens,
  };
}

/**
 * Lightweight existence check used to flag whether a submission was just
 * created or already existed (re-launch). Avoids reading the full doc.
 */
async function getExistingFlag(submissionId: string): Promise<boolean> {
  const { getDoc, doc: docFn } = await import('firebase/firestore');
  const snap = await getDoc(docFn(db, 'eprSubmissions', submissionId));
  return snap.exists();
}

// ═══════════════════════════════════════════════════════════════════
// Step 2 launcher — 360 Review
// ═══════════════════════════════════════════════════════════════════

export interface Step2ReviewerEntry {
  reviewerId: string;
  reviewerEmail: string;
  reviewerName?: string;
  relationship: Review360Relationship;
}

export interface Step2RevieweeInput {
  revieweeId: string;
  revieweeName: string;
  reviewers: Step2ReviewerEntry[];
}

export interface LaunchStep2Input {
  cycleId: string;
  reviewees: Step2RevieweeInput[];
  cycleExpiryConfig?: CycleMagicLinkExpiry;
  templateReviewerVisibility?: Partial<Record<Review360Relationship, ReviewerVisibility>>;
  advanceCycleStep?: boolean;
}

export interface LaunchStep2Result {
  cycleId: string;
  reviewerTokens: BatchIssueResult[];
  /** One entry per (reviewee, reviewer) — used by HR to copy links. */
  panelEntries: Array<{
    revieweeId: string;
    revieweeName: string;
    reviewerId: string;
    reviewerEmail: string;
    relationship: Review360Relationship;
    submissionId: string;
    token: string;
    url: string;
  }>;
}


/**
 * Launches Step 2 by issuing one 360 token per (reviewee, reviewer) pair.
 * The submission doc is created lazily on first save by the form page.
 *
 * Submission ID convention: `${cycleId}_${revieweeId}_${randomHex8}`. The
 * leading segments make HR-side queries easy; the random suffix prevents
 * collisions when one reviewer reviews the same reviewee in multiple cycles
 * (defence in depth — cycleId already separates).
 */
export async function launchStep2(input: LaunchStep2Input): Promise<LaunchStep2Result> {
  const panel: Parameters<typeof issue360ReviewerTokens>[0]['panel'] = [];
  const panelEntries: LaunchStep2Result['panelEntries'] = [];

  for (const reviewee of input.reviewees) {
    for (const rev of reviewee.reviewers) {
      const submissionId = makeSubmissionId(input.cycleId, reviewee.revieweeId);
      panel.push({
        reviewerId:    rev.reviewerId,
        reviewerEmail: rev.reviewerEmail,
        reviewerName:  rev.reviewerName,
        relationship:  rev.relationship,
        submissionId,
        revieweeId:    reviewee.revieweeId,
        revieweeName:  reviewee.revieweeName,
      });
      panelEntries.push({
        revieweeId:    reviewee.revieweeId,
        revieweeName:  reviewee.revieweeName,
        reviewerId:    rev.reviewerId,
        reviewerEmail: rev.reviewerEmail,
        relationship:  rev.relationship,
        submissionId,
        token: '',
        url:   '',
      });
    }
  }

  const reviewerTokens = await issue360ReviewerTokens({
    cycleId: input.cycleId,
    cycleExpiryConfig: input.cycleExpiryConfig,
    templateReviewerVisibility: input.templateReviewerVisibility,
    panel,
  });

  // Stitch tokens back into panel entries (preserved order)
  for (let i = 0; i < reviewerTokens.length; i++) {
    panelEntries[i].token = reviewerTokens[i].token;
    panelEntries[i].url   = reviewerTokens[i].url;
  }

  if (input.advanceCycleStep !== false) {
    await updateDoc(doc(db, 'reviewCycles', input.cycleId), {
      currentStep: 2,
      updatedAt: serverTimestamp(),
    });
  }

  return { cycleId: input.cycleId, reviewerTokens, panelEntries };
}


function makeSubmissionId(cycleId: string, revieweeId: string): string {
  const arr = new Uint8Array(4);
  crypto.getRandomValues(arr);
  const suffix = Array.from(arr, b => b.toString(16).padStart(2, '0')).join('');
  return `${cycleId}_${revieweeId}_${suffix}`;
}
